import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../../shared/hooks/useSubscription';
import { manageSubscription } from '../../../shared/services/revenueCatService';
import { authService } from '../../../shared/services/authService';
import { chatApi } from '../../../shared/api/chatApi';
import { ReadingSettings } from './ReadingSettings';
import { SubscriptionSection } from './SubscriptionSection';
import { AccountSection } from './AccountSection';
import { ChatSection } from './ChatSection';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { Header } from '../../../shared/components/layout/Header';
import { PageHeader } from '../../../shared/components/layout/pageHeader';
import { useErrorHandler } from '../../../shared/hooks/useErrorHandler';
import type { SubscriptionPlatformInfo } from '../../../shared/types/subscription';
import { Modal } from '../../../shared/components/layout/Modal';
import { useChatStore } from '../../../shared/store/chatStore';

interface Profile {
    first_name: string;
    last_name: string;
    denomination: string;
    preferred_bible: string;
}

export default function Account() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showDeleteChatsConfirm, setShowDeleteChatsConfirm] = useState(false);
    const [showSubscriptionWarning, setShowSubscriptionWarning] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDeletingChats, setIsDeletingChats] = useState(false);
    const { isActive: hasActiveSubscription, activeSubscription } = useSubscription();
    const [profile, setProfile] = useState<Profile>({
        first_name: '',
        last_name: '',
        denomination: '',
        preferred_bible: '',
    });
    const { user, signOut } = useAuth();
    const { handleAsyncError } = useErrorHandler();

    useEffect(() => {
        getProfile();
    }, []);

    async function getProfile() {
        try {
            const result = await authService.getProfile();
            if (result?.profile) {
                console.log('Got profile:', result.profile);
                setProfile(result.profile);
            } else if (result?.error) {
                throw result.error;
            }
        } catch (error) {
            handleAsyncError(Promise.reject(error), {
                severity: 'medium',
                userMessage: 'Unable to load profile. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    }

    async function updateProfile(updates: Partial<Profile>) {
        setSaving(true);
        try {
            console.log('Updating profile with:', updates);
            const result = await authService.updateProfile(updates);
            if (result?.profile) {
                console.log('Profile updated:', result.profile);
                setProfile(result.profile);
            } else if (result?.error) {
                throw result.error;
            }
        } catch (error) {
            handleAsyncError(Promise.reject(error), {
                severity: 'medium',
                userMessage: 'Unable to update profile. Please try again.'
            });
        } finally {
            setSaving(false);
        }
    }

    async function redirectToCustomerPortal() {
        try {
            const url = await manageSubscription();
            window.location.href = url;
        } catch (error) {
            handleAsyncError(Promise.reject(error), {
                severity: 'medium',
                userMessage: 'Unable to access subscription portal. Please try again.'
            });
        }
    }

    async function handleDeleteRequest() {
        if (hasActiveSubscription) {
            setShowSubscriptionWarning(true);
            return;
        }
        setShowDeleteConfirm(true);
    }

    async function handleDeleteAccount() {
        setIsDeleting(true);
        try {
            await authService.deleteAccount();
            await signOut();
            navigate('/');
        } catch (error) {
            handleAsyncError(Promise.reject(error), {
                severity: 'high',
                userMessage: 'Unable to delete account. Please try again.'
            });
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    }

    function handleDeleteChats() {
        setIsDeletingChats(true);
        chatApi.deleteAllChats()
            .then(() => useChatStore.getState().clearState())
            .then(() => setShowDeleteChatsConfirm(false))
            .catch(error => {
                handleAsyncError(Promise.reject(error), {
                    severity: 'high',
                    userMessage: 'Unable to delete chats. Please try again.'
                });
            })
            .finally(() => {
                setIsDeletingChats(false);
            });
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-[#F1EAE1]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#361E06]" />
            </div>
        );
    }

    return (
        <div className="min-h-screen">
            <Header />
            <div className="max-w-2xl mx-auto px-4 py-12">
                <PageHeader
                    headline="Account Settings"
                    subheadline="Manage your profile and subscription"
                />

                <div className="max-w-lg mx-auto space-y-8 mt-20">
                    <ReadingSettings
                        profile={profile}
                        saving={saving}
                        setProfile={setProfile}
                        updateProfile={updateProfile}
                    />

                    <SubscriptionSection
                        subscription={activeSubscription ?? null}
                        onManageSubscription={redirectToCustomerPortal}
                        onViewPlans={() => navigate('/pricing')}
                    />

                    <ChatSection
                        onDeleteAllChats={() => setShowDeleteChatsConfirm(true)}
                    />

                    <AccountSection
                        onLogout={signOut}
                        onDeleteRequest={handleDeleteRequest}
                    />
                </div>

                <Modal
                    isOpen={showSubscriptionWarning}
                    onClose={() => setShowSubscriptionWarning(false)}
                    title="Active Subscription"
                    variant="confirm"
                    confirmProps={{
                        message: "You have an active subscription. Please cancel your subscription before deleting your account.",
                        onConfirm: redirectToCustomerPortal,
                        confirmLabel: "Manage Subscription",
                        cancelLabel: "Close",
                        confirmStyle: "primary"
                    }}
                >
                    <></>
                </Modal>

                <Modal
                    isOpen={showDeleteConfirm}
                    onClose={() => setShowDeleteConfirm(false)}
                    title="Delete Account"
                    variant="confirm"
                    confirmProps={{
                        message: "Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently deleted.",
                        onConfirm: handleDeleteAccount,
                        confirmLabel: "Delete Account",
                        cancelLabel: "Cancel",
                        confirmStyle: "danger",
                        isLoading: isDeleting
                    }}
                >
                    <></>
                </Modal>

                <Modal
                    isOpen={showDeleteChatsConfirm}
                    onClose={() => setShowDeleteChatsConfirm(false)}
                    title="Delete All Chats"
                    variant="confirm"
                    confirmProps={{
                        message: "Are you sure you want to delete all your chats? This action cannot be undone.",
                        onConfirm: () => handleDeleteChats(),
                        confirmLabel: "Delete All Chats",
                        cancelLabel: "Cancel",
                        confirmStyle: "danger",
                        isLoading: isDeletingChats
                    }}
                >
                    <></>
                </Modal>
            </div>
        </div>
    );
}